import React from "react";
import styled from "styled-components/macro";
import { Typography, Button } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { Trans } from "react-i18next";
import { useGrammarExplanation } from "queries/grammarExplanation";
import TypingDots from "./TypingDots";
import { containsSmallAlphabetChars, SMALL_ALPHABET_FONT_SIZE } from "../../tools/smallAlphabetDetection";
import { AssistantMode } from "./__generated__/AssistantMutation.graphql";

type Props = {
  text: string;
  correctedText: string;
  language?: string;
  responseLanguage?: string;
  mode?: AssistantMode;
  context?: string;
};

const RetryButton = styled(Button)`
  padding: 4px 8px !important;
  font-size: 0.75rem !important;
  margin-top: 8px !important;
`;

export const GrammarExplanation = ({ text, correctedText, language, responseLanguage, context, mode }: Props) => {
  const { explanation, isLoading, error, retry } = useGrammarExplanation({
    text,
    correctedText,
    language,
    responseLanguage,
    context,
    mode
  });

  if (isLoading) {
    return <TypingDots />;
  }

  const hasValidExplanation = explanation && explanation.trim().length > 0;
  if (error || !hasValidExplanation) {
    return (
      <RetryButton
        variant="outlined"
        size="small"
        color="error"
        onClick={() => retry()}
        startIcon={<Refresh fontSize="small" />}
      >
        <Trans>Retry</Trans>
      </RetryButton>
    );
  }

  const hasSmallAlphabet = containsSmallAlphabetChars(explanation);
  return (
    <Typography variant="body2" sx={hasSmallAlphabet ? { fontSize: SMALL_ALPHABET_FONT_SIZE } : undefined}>
      {explanation}
    </Typography>
  );
};

export default GrammarExplanation;
